/**
 * JSON-LD structured data helpers for SEO
 */

export interface ProductJsonLdProps {
  name: string;
  description?: string;
  images: string[];
  price: number;
  priceCurrency: string;
  availability: "InStock" | "OutOfStock" | "PreOrder";
  url: string;
  brand?: string;
  sku?: string;
}

export interface LocalBusinessJsonLdProps {
  name: string;
  description?: string;
  url: string;
  image?: string;
  address?: {
    streetAddress?: string;
    addressLocality?: string; // city
    addressRegion?: string; // region/county
    addressCountry?: string;
  };
  telephone?: string;
  email?: string;
  geo?: {
    latitude: number;
    longitude: number;
  };
}

/**
 * Generate Product structured data for a product page
 */
export function createProductJsonLd(props: ProductJsonLdProps): Record<string, unknown> {
  const {
    name,
    description,
    images,
    price,
    priceCurrency,
    availability,
    url,
    brand,
    sku,
  } = props;

  const availabilityUrl = {
    InStock: "https://schema.org/InStock",
    OutOfStock: "https://schema.org/OutOfStock",
    PreOrder: "https://schema.org/PreOrder",
  };

  return {
    "@context": "https://schema.org",
    "@type": "Product",
    name,
    ...(description && { description }),
    image: images.length > 0 ? images : undefined,
    ...(sku && { sku }),
    ...(brand && {
      brand: {
        "@type": "Brand",
        name: brand,
      },
    }),
    offers: {
      "@type": "Offer",
      price: price.toFixed(2),
      priceCurrency,
      availability: availabilityUrl[availability],
      url,
    },
  };
}

/**
 * Generate LocalBusiness structured data for a producer page
 */
export function createLocalBusinessJsonLd(props: LocalBusinessJsonLdProps): Record<string, unknown> {
  const {
    name,
    description,
    url,
    image,
    address,
    telephone,
    email,
    geo,
  } = props;

  return {
    "@context": "https://schema.org",
    "@type": "LocalBusiness",
    "@id": url,
    name,
    ...(description && { description }),
    url,
    ...(image && { image }),
    ...(telephone && { telephone }),
    ...(email && { email }),
    ...(address && {
      address: {
        "@type": "PostalAddress",
        ...(address.streetAddress && { streetAddress: address.streetAddress }),
        ...(address.addressLocality && { addressLocality: address.addressLocality }),
        ...(address.addressRegion && { addressRegion: address.addressRegion }),
        addressCountry: address.addressCountry || "SE",
      },
    }),
    ...(geo && {
      geo: {
        "@type": "GeoCoordinates",
        latitude: geo.latitude,
        longitude: geo.longitude,
      },
    }),
  };
}

export interface EventJsonLdProps {
  name: string;
  startDate: string;
  endDate?: string | null;
  description?: string | null;
  url: string;
  image?: string | null;
  /** Practical meeting location label (address, city, etc.) */
  locationName?: string | null;
  /** Organizer display name */
  organizerName?: string | null;
  /** Price in öre (100 öre = 1 SEK). Pass null/undefined for free events. */
  priceOre?: number | null;
}

/**
 * Generate Event structured data for an event detail page.
 * Google uses this for Event rich results (date, location, price in SERP).
 * https://developers.google.com/search/docs/appearance/structured-data/event
 */
export function createEventJsonLd(props: EventJsonLdProps): Record<string, unknown> {
  const { name, startDate, endDate, description, url, image, locationName, organizerName, priceOre } = props;

  const jsonLd: Record<string, unknown> = {
    "@context": "https://schema.org",
    "@type": "Event",
    name,
    startDate,
    eventStatus: "https://schema.org/EventScheduled",
    eventAttendanceMode: "https://schema.org/OfflineEventAttendanceMode",
    url,
  };

  if (endDate) jsonLd.endDate = endDate;
  if (description) jsonLd.description = description;
  if (image) jsonLd.image = [image];

  if (locationName) {
    jsonLd.location = {
      "@type": "Place",
      name: locationName,
    };
  }

  if (organizerName) {
    jsonLd.organizer = {
      "@type": "Organization",
      name: organizerName,
      url: "https://farmly.se",
    };
  }

  if (priceOre != null) {
    jsonLd.offers = {
      "@type": "Offer",
      price: (priceOre / 100).toFixed(2),
      priceCurrency: "SEK",
      availability: "https://schema.org/InStock",
      url,
    };
  }

  return jsonLd;
}

/**
 * Generate BreadcrumbList structured data
 */
export function createBreadcrumbJsonLd(
  items: Array<{ name: string; url: string }>
): Record<string, unknown> {
  return {
    "@context": "https://schema.org",
    "@type": "BreadcrumbList",
    itemListElement: items.map((item, index) => ({
      "@type": "ListItem",
      position: index + 1,
      name: item.name,
      item: item.url,
    })),
  };
}
